import { Private_Routes } from '@common/url';
import { UserTypes } from '@common/UserTypes';
import { Alert, Button, Stack, StackItem, Text, theme } from '@prophecy/ui';
import { AlertTriangleIcon, BellAIcon } from '@prophecy/ui-v3/Icons';
import { tokens as AlertTokens } from '@prophecy/ui/Alert/tokens';
import { useTimedFlag } from '@prophecy/utils/react/hooks';
import { noop } from 'lodash-es';
import styled from 'styled-components';

import { useAppMetadata } from '../context/appMetadata';
import { useCreditsLabel } from '../context/credits';
import { getCreditConsumption } from '../data/apis/api';
import { useRestQuery } from '../data/util';

const IconContainer = styled.div``;

type CreditConsumption = {
  creditsUsedPercentage: number;
  projectedExhaustionDate?: string;
};

export function CreditUsageNotification({ isAbout }: { isAbout?: boolean }) {
  const creditLabelWithOEM = useCreditsLabel();
  const { data } = useRestQuery<CreditConsumption>(['get-credit-consumption'], () => getCreditConsumption(), {
    refetchInterval: 5 * 60 * 1000,
    onError: noop
  });

  const { creditsUsedPercentage, projectedExhaustionDate } = data?.data || {
    creditsUsedPercentage: 0,
    projectedExhaustionDate: undefined
  };

  const [snoozeNotice, toggleSnooze] = useTimedFlag(15 * 60 * 1000);

  const userInfo = useAppMetadata()?.user;
  const isAdmin = userInfo?.type === UserTypes.ClusterAdmin || userInfo?.type === UserTypes.Support;

  if (creditsUsedPercentage < 80 || snoozeNotice) {
    return null;
  }

  const isWarning = creditsUsedPercentage < 99.99;
  return (
    <Alert
      banner
      variant={isWarning ? 'warning' : 'error'}
      key={'creditUsageNotification'}
      closable={isWarning}
      onClose={() => toggleSnooze(true)}
      showIcon
      alignY='center'
      icon={
        <IconContainer>
          {isWarning ? (
            <BellAIcon type='default' color={AlertTokens.Alert.warning.iconColor} data-state='warning' />
          ) : (
            <AlertTriangleIcon type='default' color={AlertTokens.Alert.error.iconColor} data-state='error' />
          )}
        </IconContainer>
      }>
      <Stack direction='horizontal' alignY='center'>
        {isWarning ? (
          <div>
            <Text level='sm' weight={theme.fontWeight.semiBold} tone={theme.textColor.secondary}>
              You've used {Math.floor(creditsUsedPercentage)}% of your {creditLabelWithOEM}s.
            </Text>{' '}
            <Text level='sm' tone={theme.textColor.tertiary}>
              {projectedExhaustionDate ? `We project you'll use them all by ${projectedExhaustionDate}. ` : ''}Please
              notify your administrator.
            </Text>
          </div>
        ) : (
          <div>
            <Text level='sm' weight={theme.fontWeight.semiBold} tone={theme.textColor.secondary}>
              You’re out of {creditLabelWithOEM}s.
            </Text>{' '}
            <Text level='sm' tone={theme.textColor.tertiary}>
              Please request additional {creditLabelWithOEM}s from your Administrator.
            </Text>
          </div>
        )}
        <StackItem grow='1' />
        {isAdmin && (
          <Button
            size='s'
            variant='primary'
            elementType='link'
            to={Private_Routes.Settings.tab.getUrl({ tab: 'usage' }, { 'request-credits': 'true' })}
            data-test-id='credit-usage-request-more-button'>
            Request More {creditLabelWithOEM}s
          </Button>
        )}
      </Stack>
    </Alert>
  );
}
